<script setup lang="ts">
import ScrollPanel from "primevue/scrollpanel";
import { useThreadStore } from "@/stores/thread.store";
import { ref, computed, watch, nextTick, onUnmounted, type ComponentPublicInstance } from "vue";
import Message from "@/components/messaging/Message.vue";
import SendMessageForm from "@/components/messaging/SendMessageForm.vue";
import type { Message as ApiMessage } from "@/api/javaapi/models/message";
import { ThreadsApi, Configuration } from "@/api/javaapi";
import { useAuthStore } from "@/stores/auth.store";

const props = defineProps<{ threadId: number }>();
const threadStore = useThreadStore();
const authStore = useAuthStore();

// Use store's openThreads for messages
const thread = computed(() => threadStore.getOpenThread(props.threadId));
const messages = computed(() => thread.value?.messages ?? []);
const initialUnreadStatus = ref<Map<number, boolean>>(new Map());
const isMounted = ref(true);

const scrollPanel = ref<ComponentPublicInstance<typeof ScrollPanel> | null>(null);
const sendMessageForm = ref<InstanceType<typeof SendMessageForm> | null>(null);
let observer: IntersectionObserver | null = null;

const threadApi = new ThreadsApi(
  new Configuration({ accessToken: authStore.token ?? undefined })
);

const syncThreadNewStatus = async () => {
  if (
    thread.value?.newStatus &&
    messages.value.length > 0 &&
    messages.value.every((m) => m.readStatus)
  ) {
    try {
      await threadApi.markThreadAsRead(props.threadId);
      const updatedThread = threadStore.getOpenThread(props.threadId);
      if (updatedThread) {
        updatedThread.newStatus = false;
        threadStore.updateThreadInList(updatedThread);
      }
    } catch (error) {
      console.error("Failed to mark thread as read:", error);
    }
  }
};

const scrollToOldestUnread = async () => {
  if (!isMounted.value) return;
  await nextTick();
  const scrollContainer = scrollPanel.value?.$el.querySelector(".p-scrollpanel-content") as HTMLElement;
  if (!scrollContainer) return;

  if (observer) observer.disconnect();

  const unreadMessages = messages.value.filter(m => !m.readStatus);
  if (unreadMessages.length > 0) {
    const oldestUnreadMessage = unreadMessages[0];
    const targetIndex = messages.value.indexOf(oldestUnreadMessage);
    const messageElements = scrollContainer.querySelectorAll("[data-message-id]");
    const targetElement = messageElements[targetIndex];
    if (targetElement instanceof HTMLElement) {
      targetElement.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  } else {
    scrollContainer.scrollTop = scrollContainer.scrollHeight;
  }

  await nextTick();
  setupIntersectionObserver();
};

const scrollToBottom = async () => {
  if (!isMounted.value) return;
  await nextTick();
  const scrollContainer = scrollPanel.value?.$el.querySelector(".p-scrollpanel-content") as HTMLElement;
  if (scrollContainer) {
    scrollContainer.scrollTop = scrollContainer.scrollHeight;
  }
};

const isScrolledToBottom = () => {
  if (!scrollPanel.value) return false;
  const scrollContainer = scrollPanel.value.$el.querySelector(".p-scrollpanel-content") as HTMLElement;
  if (!scrollContainer) return false;
  const scrollBottom = scrollContainer.scrollTop + scrollContainer.clientHeight;
  return Math.abs(scrollContainer.scrollHeight - scrollBottom) < 10;
};

const updateMessageList = (message: ApiMessage, isNew: boolean) => {
  const currentThread = thread.value;
  if (!currentThread) return;

  const updatedMessages = [...(currentThread.messages ?? [])];
  const index = updatedMessages.findIndex((m) => m.id === message.id);
  if (index !== -1) {
    updatedMessages[index] = message;
  } else if (isNew) {
    updatedMessages.push(message);
  }

  const updatedThread = { ...currentThread, messages: updatedMessages };
  threadStore.updateThreadInList(updatedThread);
};

const handleMessageSuccess = (data: { tempId: number; message: ApiMessage }) => {
  const { tempId, message } = data;
  const currentThread = thread.value;
  if (!currentThread) return;

  // Replace the temporary message with the actual message
  const updatedMessages = currentThread.messages?.map((m) =>
    m.id === tempId ? { ...message } : m
  ) ?? [];

  const updatedThread = { ...currentThread, messages: updatedMessages };
  threadStore.updateThreadInList(updatedThread);
};
const handleNewMessage = (tempMessage: ApiMessage) => updateMessageList(tempMessage, true);
const handleMessageFailed = (failedMessage: ApiMessage) => updateMessageList(failedMessage, false);
const handleUpdateMessage = (updatedMessage: ApiMessage) => updateMessageList(updatedMessage, false);
const handleRetryMessage = (message: ApiMessage) => sendMessageForm.value?.retryMessage(message) || console.error("SendMessageForm ref is not available");

const markMessageAsRead = async (messageId: number) => {
  try {
    await threadApi.markMessageAsRead(Number(props.threadId), messageId);
    const currentThread = thread.value;
    if (currentThread) {
      const updatedMessages = currentThread.messages?.map(m =>
        m.id === messageId ? { ...m, readStatus: true } : m
      ) ?? [];
      const updatedThread = { ...currentThread, messages: updatedMessages };
      threadStore.updateThreadInList(updatedThread);
      await syncThreadNewStatus();
    }
  } catch (error) {
    console.error("Failed to mark message as read:", error);
  }
};

const setupIntersectionObserver = () => {
  if (!isMounted.value) return;
  const scrollContainer = scrollPanel.value?.$el.querySelector(".p-scrollpanel-content") as HTMLElement;
  if (!scrollContainer) return;

  if (observer) observer.disconnect();

  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach(async (entry) => {
        if (entry.isIntersecting) {
          const messageId = Number(entry.target.getAttribute("data-message-id"));
          if (!isNaN(messageId)) {
            const message = messages.value.find((m) => m.id === messageId);
            if (message && !message.readStatus) {
              await markMessageAsRead(messageId);
            }
          }
        }
      });
    },
    { 
      root: scrollContainer, 
      threshold: 0.5,
      rootMargin: "0px"
    }
  );

  const messageElements = scrollContainer.querySelectorAll("[data-message-id]");
  messageElements.forEach((el) => observer?.observe(el));
};

// Watch threadId changes, including initial mount
watch(() => props.threadId, async (newId, oldId) => {
  if (!isMounted.value || newId === oldId) return;
  if (observer) observer.disconnect();
  initialUnreadStatus.value = new Map();
  if (oldId) {
    threadStore.closeThread(oldId);
  }
  const thread = await threadStore.openThread(props.threadId);
  if (thread) {
    initialUnreadStatus.value = new Map(thread.messages?.map(m => [m.id!, !m.readStatus]) ?? []);
    await syncThreadNewStatus();
  }
  scrollToOldestUnread();
}, { immediate: true });

// Watch for new messages and scroll if at bottom
watch(() => messages.value.length, (newLength, oldLength) => {
  if (newLength > oldLength && isScrolledToBottom()) {
    scrollToBottom();
  }
});

onUnmounted(() => {
  isMounted.value = false;
  if (observer) observer.disconnect();
  threadStore.closeThread(props.threadId);
});
</script>

<template>
  <div class="flex flex-col h-full bg-white dark:bg-gray-800">
    <ScrollPanel ref="scrollPanel" class="flex-grow" style="height: 0px;">
      <div class="p-4 space-y-4">
        <Message
          v-for="message in messages"
          :key="message.id"
          :message="message"
          :initially-unread="initialUnreadStatus.get(message.id!) ?? false"
          @retry-message="handleRetryMessage"
          @mark-as-read="markMessageAsRead"
          :data-message-id="message.id"
        />
      </div>
    </ScrollPanel>
    <div class="flex-shrink-0">
      <SendMessageForm
        ref="sendMessageForm"
        id="send-message"
        class="border-t border-gray-200 dark:border-gray-700"
        :threadId="props.threadId"
        @send-message="handleNewMessage"
        @send-message-success="handleMessageSuccess"
        @send-message-failed="handleMessageFailed"
        @update-message="handleUpdateMessage"
      />
    </div>
  </div>
</template>